import React from 'react';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { Controller } from 'react-hook-form';
import { TextField } from '@mui/material';
import { styled } from '@mui/material/styles';
import { ko } from 'date-fns/locale';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import CalendarIcon from '../components/CalendarIcon';

dayjs.extend(utc);
dayjs.extend(timezone);

// 프로젝트 표준 외곽선: 기본 회색 → hover/focus teal.
// CommonDatePicker와 동일한 스타일을 사용 — 일관된 입력 디자인.
const StyledTextField = styled(TextField)({
  '& .MuiOutlinedInput-root': {
    borderRadius: '8px',
    '& fieldset': { borderColor: '#D9D9D9' },
    '&:hover fieldset': { borderColor: '#58B9C1', borderWidth: '2px' },
    '&.Mui-focused fieldset': { borderColor: '#58B9C1' },
  },
});

const KOREAN_DAYS = ['일', '월', '화', '수', '목', '금', '토'];
const DAY_ABBR_INDEX = { Su: 0, Mo: 1, Tu: 2, We: 3, Th: 4, Fr: 5, Sa: 6 };

// MUI x-date-pickers의 day 인자는 영문 약어("Mo", "Tu" ...) 또는 0-6 인덱스로 들어올 수 있다.
const formatKoreanWeekday = day => {
  if (typeof day === 'string' && day in DAY_ABBR_INDEX) {
    return KOREAN_DAYS[DAY_ABBR_INDEX[day]];
  }
  if (typeof day === 'number' && day >= 0 && day <= 6) {
    return KOREAN_DAYS[day];
  }
  return day;
};

const DATE_FORMAT = 'yyyy-MM-dd';
const TIMEZONE = 'Asia/Seoul';

export const FormInputDate = ({
  name,
  control,
  variant,
  disabled,
  minDate = null,
  maxDate = null,
  placeholder,
  renderInput,
  onChange: onChangeProp,
}) => {
  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ko}>
      <Controller
        name={name}
        control={control}
        disabled={disabled}
        render={({ field }) => {
          // 포커스가 벗어날 때 현재 값이 유효한지 확인 — 1900년 이전이거나 잘못된 날짜는 빈값으로 정리.
          const handleBlur = () => {
            if (field.value && (!dayjs(field.value).isValid() || dayjs(field.value).year() < 1900)) {
              field.onChange(null);
            }
          };

          const handleChange = date => {
            const newValue = date ? dayjs(date).tz(TIMEZONE).toDate() : null;
            field.onChange(newValue);
            if (onChangeProp) onChangeProp(newValue);
          };

          return (
            <DatePicker
              disabled={disabled}
              fullWidth
              minDate={minDate}
              maxDate={maxDate}
              inputFormat={DATE_FORMAT}
              variant={variant}
              autoOk
              showDaysOutsideCurrentMonth
              components={{ OpenPickerIcon: CalendarIcon }}
              componentsProps={{
                actionBar: {
                  actions: ['today'],
                },
              }}
              localeText={{
                cancelButtonLabel: '취소',
                clearButtonLabel: '초기화',
                okButtonLabel: '확인',
                todayButtonLabel: '오늘',
              }}
              dayOfWeekFormatter={formatKoreanWeekday}
              value={field.value ? dayjs(field.value).tz(TIMEZONE).toDate() : null}
              onChange={handleChange}
              renderInput={params => {
                const enhancedParams = {
                  ...params,
                  inputProps: {
                    ...params.inputProps,
                    placeholder: placeholder ?? params.inputProps?.placeholder,
                  },
                  onBlur: e => {
                    if (params.onBlur) params.onBlur(e);
                    handleBlur();
                  },
                };

                // 호출부에서 renderInput을 넘기면 그걸 우선 (커스텀 스타일링 케이스).
                if (renderInput) return renderInput(enhancedParams);

                // 기본은 CommonDatePicker와 동일한 StyledTextField.
                return <StyledTextField color="info" size="small" {...enhancedParams} />;
              }}
            />
          );
        }}
      />
    </LocalizationProvider>
  );
};
